/**
 * The chosen profile's collection under 'Coleta' (frame 86:2 `resumo`): per media kind, each
 * period's summary in its own colour, or "sem coleta". `media: null` (no profile chosen yet)
 * renders nothing.
 */
import { ClockFading, FileText, Repeat2, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { z } from 'zod';

import { HintedIcon } from '../../../chrome/hint';
import { Chip } from '../../../ui/chip';
import { mediaKindSchema, profileMediaSchema } from '../../control/pages/Catalogue';
import { PeriodSummary } from '../../control/pages/profile/PeriodSummary';
import { aat } from './addAccountMessages';

type MediaKind = z.infer<typeof mediaKindSchema>;
type ProfileMedia = z.infer<typeof profileMediaSchema>;

// 86:2 orders the row POST, STORY, REEL: a document, a fading clock, a repeat.
const KIND_ORDER: MediaKind[] = ['POST', 'STORY', 'REEL'];

const KIND_ICON: Record<MediaKind, LucideIcon> = {
  POST: FileText,
  REEL: Repeat2,
  STORY: ClockFading,
};

export function CollectionChips(props: { media: ProfileMedia[] | null }): ReactNode {
  const media = props.media;
  if (media === null) return null;
  return (
    <ul aria-label={aat('collection.summary')} className="flex flex-col gap-1.5">
      {KIND_ORDER.map((kind) => {
        const row = media.find((item) => item.media_kind === kind);
        const collecting = row !== undefined && row.enabled !== 'off';
        const Icon = KIND_ICON[kind];
        const kindLabel = aat(`collection.chip.${kind}`);
        return (
          <li key={kind} className="flex flex-wrap items-center gap-1.5">
            <HintedIcon label={kindLabel} className="size-4 text-muted-foreground">
              <Icon aria-hidden className="size-4" />
            </HintedIcon>
            {!collecting ? (
              <Chip tone="neutral" size="sm">
                {aat('collection.chip.noCollection')}
              </Chip>
            ) : kind === 'STORY' ? (
              <PeriodSummary
                frequencyOnly
                period={{
                  frequency_seconds: row.story_interval_seconds,
                  duration_seconds: null,
                  color: null,
                }}
              />
            ) : (
              row.periods.map((period, index) => <PeriodSummary key={index} period={period} />)
            )}
          </li>
        );
      })}
    </ul>
  );
}
